import { storage } from 'design-system';
import { useMemo, useEffect, useReducer, useCallback } from 'react';

import axios, { endpoints } from 'src/services/axios';
import { getProductRoles, convertPermission } from 'src/services/role';
import { User, Product, getAccessToken, getUserProfile, logoutForBackend } from 'src/services/user';

import { toLogin } from '../../../utils';
import { AuthContext } from './auth-context';
import { setSession, isValidToken } from './utils';
import { AuthUserType, ActionMapType, AuthStateType } from '../../types';

// ----------------------------------------------------------------------
/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */
// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const STORAGE_KEY = 'accessToken';
export const STORAGEREFRESHTOKENKEY = 'refreshToken';
export const ASSET_HOST_KEY = 'assetHost';

type Props = {
  children: React.ReactNode;
  href: string;
};

export function AuthProvider({ children, href }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getUserInfo = useCallback(async () => {
    const user: User = await getUserProfile();
    user.roles = getProductRoles(user.products, Product.AMOEBA);
    user.permissions = convertPermission(user.roles);
    dispatch({
      type: Types.INITIAL,
      payload: {
        user: {
          ...user,
        },
      },
    });
  }, []);

  const initialize = useCallback(async () => {
    try {
      const url = new URL(href);
      const refreshToken = url.searchParams.get(STORAGEREFRESHTOKENKEY);
      let accessToken = localStorage.getItem(STORAGE_KEY);
      if (refreshToken) {
        url.searchParams.delete(STORAGEREFRESHTOKENKEY);

        const result = await getAccessToken(Product.AMOEBA, refreshToken || '');
        accessToken = result.token;
        localStorage.setItem(STORAGEREFRESHTOKENKEY, result.refreshToken);
        window.history.replaceState({}, '', url.toString());
      }

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const configRes = await axios.get(endpoints.config);
        await getUserInfo();

        if (configRes.data?.assetHost) {
          storage.set(ASSET_HOST_KEY, configRes.data.assetHost);
        }
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      // 错误处理
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGOUT
  const logout = useCallback(async () => {
    if (state.user?.username) {
      try {
        await logoutForBackend(state.user.username);
      } catch (error) {
        console.log(error);
      }
    }
    setSession(null);
    dispatch({
      type: Types.LOGOUT,
    });
    toLogin();
  }, [state.user]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      logout,
      getUserProfile: getUserInfo,
    }),
    [logout, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
